/**
 * Reset Password Page
 *
 * User lands here after clicking the reset link in their email.
 * The token is passed as a query parameter by BetterAuth's callback.
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { AlertCircle, ArrowLeft, CheckCircle, Lock } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { BrandMark, BrandWordmark } from '../components/brand-mark'
import { Alert, Button, buttonClasses, cn, TextField } from '../components/ui'
import { resetPassword } from '../lib/auth-client'

const createSchema = (t: (key: string) => string) =>
	z
		.object({
			password: z.string().min(8, t('auth.validation.passwordMin')),
			confirmPassword: z.string(),
		})
		.refine((data) => data.password === data.confirmPassword, {
			message: t('auth.validation.passwordMismatch'),
			path: ['confirmPassword'],
		})

type FormData = z.infer<ReturnType<typeof createSchema>>

export default function ResetPasswordPage() {
	const { t } = useTranslation()
	const [searchParams] = useSearchParams()
	const token = searchParams.get('token')
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [success, setSuccess] = useState(false)
	const schema = createSchema(t)
	const form = useForm<FormData>({
		resolver: zodResolver(schema),
	})
	const { setFocus } = form
	// Move focus to the success heading once the password is changed so the
	// screen reader announces the confirmation that replaces the form.
	const successHeadingRef = useRef<HTMLHeadingElement>(null)

	useEffect(() => {
		if (success) successHeadingRef.current?.focus()
	}, [success])

	const handleSubmit = async (data: FormData) => {
		if (!token) return

		setIsLoading(true)
		setError(null)

		try {
			const result = await resetPassword({
				newPassword: data.password,
				token,
			})

			if (result.error) {
				setError(result.error.message ?? t('auth.errors.resetFailed'))
				return
			}

			setSuccess(true)
		} catch {
			setError(t('auth.errors.resetFailed'))
		} finally {
			setIsLoading(false)
		}
	}

	if (!token) {
		return (
			<div className="min-h-screen bg-base-200 flex flex-col">
				<header className="bg-base-100 border-b border-base-300 py-4">
					<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
						<Link to="/" className="flex items-center gap-2">
							<BrandMark />
							<BrandWordmark />
						</Link>
					</div>
				</header>
				<main className="flex-1 flex items-center justify-center p-4">
					<div className="card-refined w-full max-w-md p-8 text-center">
						<AlertCircle size={48} className="mx-auto mb-4 text-error" />
						<h1 className="text-2xl font-display mb-2">{t('auth.resetLinkInvalid')}</h1>
						<p className="text-base-content/60 mb-6">{t('auth.resetLinkInvalidDescription')}</p>
						<Link
							to="/auth/forgot-password"
							className={cn(buttonClasses(), 'inline-flex items-center gap-2')}
						>
							{t('auth.requestNewLink')}
						</Link>
					</div>
				</main>
			</div>
		)
	}

	return (
		<div className="min-h-screen bg-base-200 flex flex-col">
			<header className="bg-base-100 border-b border-base-300 py-4">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<Link to="/" className="flex items-center gap-2">
						<BrandMark />
						<BrandWordmark />
					</Link>
				</div>
			</header>

			<main className="flex-1 flex items-center justify-center p-4">
				<div className="card-refined w-full max-w-md p-8">
					{success ? (
						// biome-ignore lint/a11y/useSemanticElements: status live region, not an <output>
						<div className="text-center" role="status">
							<div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success/10 flex items-center justify-center">
								<CheckCircle size={32} className="text-success" />
							</div>
							<h1
								ref={successHeadingRef}
								tabIndex={-1}
								className="text-2xl font-display mb-2 outline-none"
							>
								{t('auth.passwordChanged')}
							</h1>
							<p className="text-base-content/60 mb-6">{t('auth.passwordChangedDescription')}</p>
							<Link to="/auth" className={cn(buttonClasses(), 'inline-flex items-center gap-2')}>
								<ArrowLeft size={16} />
								{t('auth.backToLogin')}
							</Link>
						</div>
					) : (
						<>
							<div className="text-center mb-8">
								<h1 className="text-2xl font-display mb-2">{t('auth.resetPasswordTitle')}</h1>
								<p className="text-base-content/60">{t('auth.resetPasswordDescription')}</p>
							</div>

							{error && (
								<Alert tone="error" className="mb-6">
									{error}
								</Alert>
							)}

							<form
								onSubmit={form.handleSubmit(handleSubmit, () => setFocus('password'))}
								className="space-y-4"
							>
								<TextField
									id="new-password"
									type="password"
									label={t('auth.newPassword')}
									placeholder={t('auth.passwordPlaceholder')}
									leadingIcon={<Lock size={18} />}
									withPasswordToggle
									showPasswordLabel={t('auth.showPassword')}
									hidePasswordLabel={t('auth.hidePassword')}
									autoComplete="new-password"
									required
									error={form.formState.errors.password?.message}
									{...form.register('password')}
								/>
								<TextField
									id="confirm-new-password"
									type="password"
									label={t('auth.confirmNewPassword')}
									placeholder={t('auth.passwordPlaceholder')}
									leadingIcon={<Lock size={18} />}
									withPasswordToggle
									showPasswordLabel={t('auth.showPassword')}
									hidePasswordLabel={t('auth.hidePassword')}
									autoComplete="new-password"
									required
									error={form.formState.errors.confirmPassword?.message}
									{...form.register('confirmPassword')}
								/>

								<Button type="submit" loading={isLoading} variant="primary" block>
									{isLoading ? t('auth.resetting') : t('auth.resetPasswordButton')}
								</Button>
							</form>
						</>
					)}
				</div>
			</main>
		</div>
	)
}
